import { useMyUser } from '@/app/hooks/data-hooks/useMyUser'
import React from 'react'
import { ApiError } from '../../lib/ApiError'

type AuthContextArgs = {
  isAuthenticated: boolean
  _id: string | null
  firstName: string | null
}

type AuthContextValue = {
  isAuthenticated: boolean
  email: string | null
  userId: string | null
  firstName: string | null
  lastName: string | null
  isLoading: boolean
  company: string | null
  roles: string[] | null
}

const AuthContext = React.createContext<AuthContextValue>({
  isAuthenticated: false,
  email: null,
  userId: null,
  firstName: null,
  lastName: null,
  isLoading: false,
  company: null,
  roles: null,
})

type AuthProviderProps = {
  children: React.ReactNode
  userInfo?: Pick<AuthContextArgs, 'firstName' | '_id'>
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { data: { data: user } = {}, isLoading } = useMyUser({
    options: {
      retry: (_failureCount, error) => {
        if (error instanceof ApiError && error.statusCode === 401) {
          return false
        }
        return true
      },
    },
  })

  const value = {
    isAuthenticated: !!user,
    email: user?.email ?? null,
    firstName: user?.firstName ?? null,
    lastName: user?.lastName ?? null,
    userId: user?._id ?? null,
    company: (user?.company as string) ?? null,
    roles: user?.roles ?? [],
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = React.useContext(AuthContext)

  return context
}

export function useIsAuthenticated() {
  const context = useAuth()
  return context.isAuthenticated
}
